import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useWorkshop } from "../../contexts/WorkshopContext";
import { ExternalCar, CreateInspectionRequest } from "../../types";
import ExternalCarService from "../../services/externalCarService";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

interface CreateInspectionProps {
  embedMode?: boolean;
  onClose?: () => void;
  onCreated?: (inspectionId: number) => void;
}

const CreateInspection: React.FC<CreateInspectionProps> = ({
  embedMode = false,
  onClose,
  onCreated,
}) => {
  const { isDarkMode } = useTheme();
  const { selectedWorkshop, isLoading: workshopLoading } = useWorkshop();
  const [formData, setFormData] = useState<CreateInspectionRequest>({
    vehicle_id: 0,
    inspection_type: "pretrip",
    mileage_start: undefined,
    mileage_end: undefined,
    fuel_level: undefined,
    brakes_ok: false,
    lights_ok: false,
    tires_ok: false,
    engine_ok: false,
    transmission_ok: false,
    steering_ok: false,
    mirrors_ok: false,
    horn_ok: false,
    windshield_ok: false,
    notes: "",
    defects_found: "",
    repairs_needed: "",
  });
  const [vehicles, setVehicles] = useState<ExternalCar[]>([]);
  const [activeVehicleIds, setActiveVehicleIds] = useState<Set<number>>(
    new Set()
  );
  const [selectedVehicle, setSelectedVehicle] = useState<ExternalCar | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [driverName, setDriverName] = useState("");
  const [mechanicName, setMechanicName] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Only load vehicles when workshop is loaded and available
    if (!workshopLoading && selectedWorkshop) {
      // Limpiar el estado anterior
      setVehicles([]);
      setSearchTerm("");
      setSelectedVehicle(null);
      setActiveVehicleIds(new Set());
      setError(null);

      loadVehicles();
      loadActiveInspections();
    } else if (!workshopLoading && !selectedWorkshop) {
      setError("Please select a workshop first");
      setLoadingVehicles(false);
    }
  }, [workshopLoading, selectedWorkshop?.id]);

  // Search vehicles with debounce when search term changes
  useEffect(() => {
    if (!selectedWorkshop) return;

    const timer = setTimeout(() => {
      if (searchTerm.trim()) {
        loadVehicles(searchTerm);
      } else {
        // If search is cleared, load all vehicles
        loadVehicles();
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, selectedWorkshop?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowVehicleDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadVehicles = async (search?: string) => {
    if (!selectedWorkshop) {
      setError("Please select a workshop first");
      setLoadingVehicles(false);
      return;
    }

    try {
      setLoadingVehicles(true);
      const response = await ExternalCarService.getAvailableCars({
        workshop_id: selectedWorkshop.id,
        limit: 1000,
        search: search || undefined,
      });
      setVehicles(response.data);
    } catch (error) {
      console.error("Error loading vehicles:", error);
      setError("Error loading vehicles from external database");
    } finally {
      setLoadingVehicles(false);
    }
  };

  const loadActiveInspections = async () => {
    if (!selectedWorkshop) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/inspections/active?workshop_id=${selectedWorkshop.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        const vehicleIds = new Set<number>(
          data.data.map((inspection: any) => Number(inspection.vehicle_id))
        );
        setActiveVehicleIds(vehicleIds);
      }
    } catch (error) {
      console.error("Error loading active inspections:", error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVehicleSelect = (vehicle: ExternalCar) => {
    setSelectedVehicle(vehicle);
    setFormData((prev) => ({ ...prev, vehicle_id: vehicle.id }));
    setShowVehicleDropdown(false);
    setSearchTerm("");
    setCurrentStep(2);
    // Auto-populate driver name if available
    if (vehicle.name_driver) {
      setDriverName(vehicle.name_driver);
    }
  };

  // Filter only by active inspections - search is handled by backend
  const filteredVehicles = vehicles.filter((v) => !activeVehicleIds.has(v.id)); // Exclude vehicles with active inspections

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedVehicle) {
      setError("Please select a vehicle");
      return;
    }

    if (!selectedWorkshop) {
      setError("Please select a workshop first");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/inspections`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({
          vehicle_id: selectedVehicle.id,
          workshop_id: selectedWorkshop.id,
          created_by: "admin", // Username instead of ID
          driver_name: driverName || null,
          mechanic_name: mechanicName || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Error creating inspection");
      }

      const data = await response.json();
      console.log("Inspection created:", data);

      if (onCreated) {
        onCreated(data.id);
      } else {
        // Redirect to edit the newly created inspection when no custom handler
        navigate(`/inspections/${data.id}/edit`);
      }
    } catch (error: any) {
      console.error("Error saving inspection:", error);
      setError(error.message || "Error saving inspection");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      navigate("/inspections");
    }
  };

  const safetyItems = [
    { key: "brakes_ok", label: "Brakes" },
    { key: "lights_ok", label: "Lights" },
    { key: "tires_ok", label: "Tires" },
    { key: "engine_ok", label: "Engine" },
    { key: "transmission_ok", label: "Transmission" },
    { key: "steering_ok", label: "Steering" },
    { key: "mirrors_ok", label: "Mirrors" },
    { key: "horn_ok", label: "Horn" },
    { key: "windshield_ok", label: "Windshield" },
  ];

  // Show loading state while workshop is being loaded
  if (workshopLoading) {
    return (
      <div className={embedMode ? "w-full" : "max-w-4xl mx-auto"}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div
              className={`inline-block animate-spin rounded-full h-12 w-12 border-b-2 ${
                isDarkMode ? "border-phoenix-400" : "border-phoenix-600"
              }`}
            ></div>
            <p
              className={`mt-4 text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Loading workshop...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedMode ? "w-full" : "max-w-4xl mx-auto"}>
      {/* Header with Progress */}
      <div className="mb-8">
        <div className="flex items-center mb-6">
          {embedMode ? (
            <button
              onClick={handleCancel}
              className={`mr-4 p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
              title="Close"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => navigate("/inspections")}
              className={`mr-4 p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
              title="Back to Inspections"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
          )}
          <div>
            <h1
              className={`text-3xl font-bold ${
                isDarkMode ? "text-gray-100" : "text-gray-900"
              }`}
            >
              Create New Inspection
            </h1>
            <p
              className={`mt-1 text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Select a vehicle and provide inspection details
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center space-x-4">
          {/* Step 1 */}
          <div className="flex items-center">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-all ${
                currentStep >= 1
                  ? isDarkMode
                    ? "bg-phoenix-600 border-phoenix-600 text-white"
                    : "bg-phoenix-600 border-phoenix-600 text-white"
                  : isDarkMode
                  ? "border-gray-600 text-gray-600"
                  : "border-gray-300 text-gray-400"
              }`}
            >
              {currentStep > 1 ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                "1"
              )}
            </div>
            <span
              className={`ml-2 text-sm font-medium ${
                currentStep >= 1
                  ? isDarkMode
                    ? "text-gray-200"
                    : "text-gray-900"
                  : isDarkMode
                  ? "text-gray-600"
                  : "text-gray-400"
              }`}
            >
              Select Vehicle
            </span>
          </div>

          {/* Divider */}
          <div
            className={`w-16 h-0.5 ${
              currentStep >= 2
                ? isDarkMode
                  ? "bg-phoenix-600"
                  : "bg-phoenix-600"
                : isDarkMode
                ? "bg-gray-700"
                : "bg-gray-300"
            }`}
          ></div>

          {/* Step 2 */}
          <div className="flex items-center">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-all ${
                currentStep >= 2
                  ? isDarkMode
                    ? "bg-phoenix-600 border-phoenix-600 text-white"
                    : "bg-phoenix-600 border-phoenix-600 text-white"
                  : isDarkMode
                  ? "border-gray-600 text-gray-600"
                  : "border-gray-300 text-gray-400"
              }`}
            >
              2
            </div>
            <span
              className={`ml-2 text-sm font-medium ${
                currentStep >= 2
                  ? isDarkMode
                    ? "text-gray-200"
                    : "text-gray-900"
                  : isDarkMode
                  ? "text-gray-600"
                  : "text-gray-400"
              }`}
            >
              Additional Details
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div
          className={`mb-6 border-l-4 px-4 py-3 rounded-r-lg flex items-start ${
            isDarkMode
              ? "bg-red-900/20 border-red-500 text-red-300"
              : "bg-red-50 border-red-500 text-red-700"
          }`}
        >
          <svg
            className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <p className="font-medium">Error</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Vehicle Selection - Step 1 */}
        <div
          className={`rounded-xl shadow-lg border transition-all ${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          } ${currentStep === 1 ? "ring-2 ring-phoenix-500" : ""}`}
        >
          <div
            className={`px-6 py-4 border-b flex items-center justify-between ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                  selectedVehicle
                    ? "bg-green-500 text-white"
                    : isDarkMode
                    ? "bg-phoenix-600 text-white"
                    : "bg-phoenix-600 text-white"
                }`}
              >
                {selectedVehicle ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  "1"
                )}
              </div>
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-gray-100" : "text-gray-900"
                }`}
              >
                Vehicle Selection
              </h2>
            </div>
            {selectedVehicle && (
              <span
                className={`text-xs font-medium px-3 py-1 rounded-full ${
                  isDarkMode
                    ? "bg-green-900/50 text-green-300"
                    : "bg-green-100 text-green-800"
                }`}
              >
                ✓ Selected
              </span>
            )}
          </div>

          <div className="p-6">
            <div className="space-y-4">
              <div className="relative">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <span className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-phoenix-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                      />
                    </svg>
                    Search and Select Vehicle{" "}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </label>

                {!selectedVehicle ? (
                  <div>
                    {loadingVehicles ? (
                      <div
                        className={`flex items-center justify-center py-12 rounded-lg border-2 border-dashed ${
                          isDarkMode
                            ? "border-gray-700 bg-gray-700/30"
                            : "border-gray-300 bg-gray-50"
                        }`}
                      >
                        <div className="text-center">
                          <div
                            className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-3 ${
                              isDarkMode
                                ? "border-phoenix-400"
                                : "border-phoenix-600"
                            }`}
                          ></div>
                          <p
                            className={`text-sm ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Loading vehicles...
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                              className={`h-5 w-5 ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </div>
                          <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Type to search by plate, number, tank, or driver name..."
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setShowVehicleDropdown(true);
                            }}
                            onFocus={() => setShowVehicleDropdown(true)}
                            className={`block w-full pl-10 pr-3 py-3 border-2 rounded-lg shadow-sm focus:outline-none transition-all ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                            }`}
                          />
                        </div>

                        {showVehicleDropdown && filteredVehicles.length > 0 && (
                          <div
                            ref={dropdownRef}
                            className={`absolute z-20 mt-2 w-full shadow-xl max-h-80 rounded-lg py-2 text-base overflow-auto border-2 ${
                              isDarkMode
                                ? "bg-gray-800 border-gray-700"
                                : "bg-white border-gray-200"
                            }`}
                          >
                            <div
                              className={`px-4 py-2 text-xs font-medium ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {filteredVehicles.length} vehicle
                              {filteredVehicles.length !== 1 ? "s" : ""} found
                            </div>
                            {filteredVehicles.slice(0, 15).map((vehicle) => (
                              <button
                                key={vehicle.id}
                                type="button"
                                onClick={() => handleVehicleSelect(vehicle)}
                                className={`w-full text-left px-4 py-3 transition-colors border-l-4 border-transparent hover:border-phoenix-500 ${
                                  isDarkMode
                                    ? "hover:bg-gray-700"
                                    : "hover:bg-gray-50"
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span
                                    className={`font-semibold text-base ${
                                      isDarkMode
                                        ? "text-gray-200"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {vehicle.plate || `#${vehicle.number}`}
                                  </span>
                                  <span
                                    className={`text-sm px-2 py-0.5 rounded ${
                                      isDarkMode
                                        ? "bg-phoenix-900/50 text-phoenix-300"
                                        : "bg-phoenix-100 text-phoenix-700"
                                    }`}
                                  >
                                    Tank: {vehicle.tank || "N/A"}
                                  </span>
                                </div>
                                <div className="flex items-center text-sm">
                                  <svg
                                    className={`w-4 h-4 mr-1.5 ${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                    />
                                  </svg>
                                  <span
                                    className={
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-600"
                                    }
                                  >
                                    {vehicle.name_driver ||
                                      "No driver assigned"}
                                  </span>
                                  <span
                                    className={`ml-auto text-xs ${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    ID: {vehicle.id}
                                  </span>
                                </div>
                              </button>
                            ))}
                            {filteredVehicles.length > 15 && (
                              <div
                                className={`px-4 py-2 text-xs text-center ${
                                  isDarkMode ? "text-gray-500" : "text-gray-400"
                                }`}
                              >
                                Showing first 15 results. Refine your search to
                                see more.
                              </div>
                            )}
                          </div>
                        )}

                        {showVehicleDropdown &&
                          searchTerm &&
                          filteredVehicles.length === 0 && (
                            <div
                              className={`mt-2 p-6 rounded-lg border-2 border-dashed text-center ${
                                isDarkMode
                                  ? "border-gray-700 bg-gray-700/30"
                                  : "border-gray-300 bg-gray-50"
                              }`}
                            >
                              <svg
                                className={`mx-auto h-12 w-12 mb-3 ${
                                  isDarkMode ? "text-gray-600" : "text-gray-400"
                                }`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <p
                                className={`text-sm font-medium ${
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                No vehicles found for "{searchTerm}"
                              </p>
                              <p
                                className={`text-xs mt-1 ${
                                  isDarkMode ? "text-gray-500" : "text-gray-400"
                                }`}
                              >
                                Try a different search term
                              </p>
                            </div>
                          )}
                      </>
                    )}
                  </div>
                ) : (
                  <div
                    className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                      isDarkMode
                        ? "bg-green-900/20 border-green-700"
                        : "bg-green-50 border-green-300"
                    }`}
                  >
                    <div className="flex items-center flex-1">
                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                          isDarkMode
                            ? "bg-green-800 text-green-300"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <div
                          className={`font-semibold text-lg ${
                            isDarkMode ? "text-green-200" : "text-green-900"
                          }`}
                        >
                          {selectedVehicle.plate ||
                            `#${selectedVehicle.number}`}
                        </div>
                        <div
                          className={`text-sm flex items-center mt-1 ${
                            isDarkMode ? "text-green-300" : "text-green-700"
                          }`}
                        >
                          <span className="font-medium">
                            Tank: {selectedVehicle.tank || "N/A"}
                          </span>
                          <span className="mx-2">•</span>
                          <span>
                            Driver: {selectedVehicle.name_driver || "N/A"}
                          </span>
                          <span className="mx-2">•</span>
                          <span className="text-xs">
                            ID: {selectedVehicle.id}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedVehicle(null);
                        setFormData((prev) => ({ ...prev, vehicle_id: 0 }));
                        setCurrentStep(1);
                      }}
                      className={`ml-4 p-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? "text-red-400 hover:text-red-300 hover:bg-red-900/30"
                          : "text-red-600 hover:text-red-800 hover:bg-red-100"
                      }`}
                      title="Change vehicle"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Details - Step 2 */}
        {selectedVehicle && (
          <div
            className={`rounded-xl shadow-lg border transition-all ${
              isDarkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            } ${currentStep === 2 ? "ring-2 ring-phoenix-500" : ""}`}
          >
            <div
              className={`px-6 py-4 border-b flex items-center ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                  isDarkMode
                    ? "bg-phoenix-600 text-white"
                    : "bg-phoenix-600 text-white"
                }`}
              >
                2
              </div>
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-gray-100" : "text-gray-900"
                }`}
              >
                Additional Details (Optional)
              </h2>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Driver Name */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <span className="flex items-center">
                      <svg
                        className={`w-4 h-4 mr-2 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      Driver Name
                    </span>
                  </label>
                  <input
                    type="text"
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    placeholder="Enter driver name"
                    className={`block w-full px-4 py-2.5 border rounded-lg shadow-sm focus:outline-none transition-all ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                    }`}
                  />
                </div>

                {/* Mechanic Name */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <span className="flex items-center">
                      <svg
                        className={`w-4 h-4 mr-2 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      Mechanic Name
                    </span>
                  </label>
                  <input
                    type="text"
                    value={mechanicName}
                    onChange={(e) => setMechanicName(e.target.value)}
                    placeholder="Enter mechanic name"
                    className={`block w-full px-4 py-2.5 border rounded-lg shadow-sm focus:outline-none transition-all ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                    }`}
                  />
                </div>
              </div>

              {/* Info Banner */}
              <div
                className={`mt-6 p-4 rounded-lg border flex items-start ${
                  isDarkMode
                    ? "bg-blue-900/20 border-blue-800"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <svg
                  className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                    isDarkMode ? "text-blue-400" : "text-blue-600"
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-blue-300" : "text-blue-800"
                    }`}
                  >
                    After creating the inspection, you'll be redirected to fill
                    out the detailed checklist items.
                  </p>
                  <p
                    className={`text-xs mt-1 ${
                      isDarkMode ? "text-blue-400" : "text-blue-600"
                    }`}
                  >
                    This includes brake checks, lights, tires, and all safety
                    components.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div
          className={`sticky bottom-0 p-4 border-t backdrop-blur-sm rounded-lg ${
            isDarkMode
              ? "bg-gray-800/95 border-gray-700"
              : "bg-white/95 border-gray-200"
          }`}
        >
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className={`px-6 py-2.5 border-2 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-all ${
                isDarkMode
                  ? "border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600 focus:ring-phoenix-400"
                  : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-phoenix-500"
              }`}
            >
              <span className="flex items-center">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Cancel
              </span>
            </button>
            <button
              type="submit"
              disabled={loading || !formData.vehicle_id}
              className={`px-8 py-2.5 border-2 border-transparent rounded-lg text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 active:scale-95 shadow-lg ${
                isDarkMode
                  ? "bg-phoenix-600 hover:bg-phoenix-700 focus:ring-phoenix-400"
                  : "bg-phoenix-600 hover:bg-phoenix-700 focus:ring-phoenix-500"
              }`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Inspection...
                </span>
              ) : (
                <span className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Create Inspection
                </span>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateInspection;
